<script setup lang="ts">
// The optional "share with" step before a Sheets export (ORP-25238). Offers only the workspace's
// Google Sheets sharing list, capped at the backend's 10 addresses; the exporter always gets the
// sheet regardless, so an empty selection is a normal export.
import { computed, ref, watch } from 'vue'

import type { SheetSharePicker } from '../../composables/useDataVisibleExportActions'
import { clampShareSelection, SHEET_SHARE_MAX_RECIPIENTS } from '../../service/sheetShare'

import { useI18n } from '@/shared/i18n'
import { Button, Modal, MultiSelect, type SelectOption } from '@/shared/ui'

const props = defineProps<{ picker: SheetSharePicker }>()

const { t } = useI18n()
const selected = ref<string[]>([])

const open = computed({
  get: () => props.picker.open.value,
  set: (value: boolean) => {
    if (!value) props.picker.dismiss()
  },
})

const options = computed<SelectOption<string>[]>(() =>
  props.picker.options.value.map((email) => ({ value: email, label: email })),
)

watch(open, (isOpen) => {
  if (isOpen) selected.value = []
})

function onSelect(value: string[]): void {
  selected.value = clampShareSelection(value, props.picker.options.value)
}

function onExport(): void {
  void props.picker.confirm([...selected.value])
}
</script>

<template>
  <Modal
    v-model="open"
    size="md"
    :title="t('reports.sheetShare.title')"
    data-testid="sheet-share-modal"
  >
    <template #body>
      <div class="flex flex-col gap-md">
        <p class="m-0 text-sm text-muted">{{ t('reports.sheetShare.intro') }}</p>
        <MultiSelect
          :model-value="selected"
          :options="options"
          :label="t('reports.sheetShare.label')"
          :placeholder="t('reports.sheetShare.placeholder')"
          :max="SHEET_SHARE_MAX_RECIPIENTS"
          searchable
          data-testid="sheet-share-select"
          @update:model-value="onSelect($event as string[])"
        />
        <p
          v-if="selected.length >= SHEET_SHARE_MAX_RECIPIENTS"
          class="m-0 text-xs text-muted"
          data-testid="sheet-share-cap"
        >
          {{ t('reports.sheetShare.cap', { max: SHEET_SHARE_MAX_RECIPIENTS }) }}
        </p>
      </div>
    </template>
    <template #buttons>
      <Button variant="soft" @click="open = false">{{ t('reports.sheetShare.cancel') }}</Button>
      <Button data-testid="sheet-share-export" @click="onExport">
        {{ t('reports.sheetShare.export') }}
      </Button>
    </template>
  </Modal>
</template>
